import { DocumentTextIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useMemo, useState } from 'react';
import PDFViewer from './PDFViewer';

export interface DocumentItem {
    id: string;
    name: string;
    url: string;
    date?: string;
}

interface DocumentViewerProps {
    /** Single URL string or array of documents. */
    documents: string | DocumentItem[];
    apiBaseUrl?: string;
    showControls?: boolean;
    className?: string;
    getAuthToken?: () => string | null;
}

function formatDate(dateStr?: string): string {
    if (!dateStr) return '';
    try {
        return new Date(dateStr).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    } catch {
        return '';
    }
}

function normalizeDocuments(input: string | DocumentItem[]): DocumentItem[] {
    if (typeof input === 'string') {
        return [{ id: '0', name: 'Document', url: input }];
    }
    return input;
}

function sortByDateDesc(docs: DocumentItem[]): DocumentItem[] {
    return [...docs].sort((a, b) => {
        if (!a.date && !b.date) return 0;
        if (!a.date) return 1;
        if (!b.date) return -1;
        return new Date(b.date).getTime() - new Date(a.date).getTime();
    });
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({
    documents,
    apiBaseUrl,
    showControls = true,
    className,
    getAuthToken
}) => {
    const sorted = useMemo(() => sortByDateDesc(normalizeDocuments(documents)), [documents]);
    const [selectedUrl, setSelectedUrl] = useState<string | null>(
        sorted.length === 1 ? sorted[0].url : null
    );

    // Reset selection when the documents prop changes (e.g. a different document set)
    useEffect(() => {
        setSelectedUrl(sorted.length === 1 ? sorted[0].url : null);
    }, [sorted]);

    // Single document or already selected — show PDF viewer
    if (selectedUrl) {
        return (
            <div className={`flex flex-col h-full ${className ?? ''}`}>
                {/* Back to list when multiple docs */}
                {sorted.length > 1 && (
                    <button
                        onClick={() => setSelectedUrl(null)}
                        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors mb-3 self-start"
                    >
                        <ChevronRightIcon className="w-3.5 h-3.5 rotate-180" />
                        All versions
                    </button>
                )}
                <div className="flex-1 min-h-0">
                    <PDFViewer
                        pdfUrl={selectedUrl}
                        apiBaseUrl={apiBaseUrl}
                        showControls={showControls}
                        className="h-full"
                        getAuthToken={getAuthToken}
                    />
                </div>
            </div>
        );
    }

    // Multiple documents — show picker
    return (
        <div className={`flex flex-col h-full ${className ?? ''}`}>
            <div className="flex-1 overflow-auto">
                <div className="flex flex-col items-center justify-center py-12 px-6">
                    <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                        <DocumentTextIcon className="w-6 h-6 text-gray-400" />
                    </div>
                    <h3 className="text-sm font-semibold text-gray-800 mb-1">
                        {sorted.length} version{sorted.length !== 1 ? 's' : ''} available
                    </h3>
                    <p className="text-xs text-gray-500 mb-6">Select a version to view</p>

                    <div className="flex flex-col gap-2 w-full max-w-md">
                        {sorted.map((doc, index) => {
                            const date = formatDate(doc.date);
                            const isLatest = index === 0;
                            return (
                                <button
                                    key={doc.id}
                                    onClick={() => setSelectedUrl(doc.url)}
                                    className="flex items-center gap-4 px-4 py-3.5 rounded-xl border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all text-left group"
                                >
                                    <div className="w-9 h-9 rounded-lg bg-gray-100 group-hover:bg-gray-200 flex items-center justify-center flex-shrink-0 transition-colors">
                                        <DocumentTextIcon className="w-4.5 h-4.5 text-gray-500" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium text-gray-800 truncate">
                                                {doc.name}
                                            </span>
                                            {isLatest && (
                                                <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded flex-shrink-0">
                                                    Latest
                                                </span>
                                            )}
                                        </div>
                                        {date && (
                                            <span className="text-xs text-gray-500 mt-0.5 block">
                                                {date}
                                            </span>
                                        )}
                                    </div>
                                    <ChevronRightIcon className="w-4 h-4 text-gray-300 group-hover:text-gray-500 flex-shrink-0 transition-colors" />
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DocumentViewer;
